import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from './ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from './ui/dialog';
import { Label } from './ui/label';
import { Search, Clock3, Download, RefreshCw, AlertTriangle, CalendarClock, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API_URL } from '../lib/apiBase';
import { downloadFile, sortRows } from '../lib/printReport';
import { Button } from './ui/button';

const getHeaders = () => {
  const token = localStorage.getItem('hrms-token');
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
};

export const CompOffRegister = ({ selectedEntity, canAdd = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [dept, setDept] = useState('all');
  const [sort, setSort] = useState('employee_list');
  const [resyncing, setResyncing] = useState(false);
  const [redating, setRedating] = useState(false);
  // Add Comp-Off (additive credit — never alters existing credits)
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [empList, setEmpList] = useState([]);
  const [empSearch, setEmpSearch] = useState('');
  const today = new Date().toISOString().slice(0, 10);
  const [addForm, setAddForm] = useState({ employee_id: '', employee_name: '', add_days: '1', comp_off_date: today, reason: '' });

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedEntity ? `?entity_id=${selectedEntity.entity_id}` : '';
      const res = await fetch(`${API_URL}/api/comp-off/register${params}`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) setRows(await res.json());
    } catch { toast.error('Failed to load comp-off register'); }
    finally { setLoading(false); }
  }, [selectedEntity]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const departments = [...new Set(rows.map(r => r.department).filter(Boolean))].sort();

  const filtered = sortRows(rows.filter(r => {
    if (filter === 'with_balance' && !(r.balance > 0)) return false;
    if (filter === 'expiring_soon' && !(r.credits || []).some(c => c.status === 'expiring_soon')) return false;
    if (filter === 'expired' && !(r.credits || []).some(c => c.status === 'expired')) return false;
    if (dept !== 'all' && (r.department || '') !== dept) return false;
    const s = search.toLowerCase();
    return !s || (r.employee_name || '').toLowerCase().includes(s) || (r.employee_id || '').toLowerCase().includes(s);
  }), sort);

  const handleExcel = async () => {
    try {
      const params = new URLSearchParams({ format: 'excel', sort });
      if (dept !== 'all') params.append('department', dept);
      if (selectedEntity) params.append('entity_id', selectedEntity.entity_id);
      await downloadFile(`${API_URL}/api/comp-off/register?${params}`, 'comp_off_register.xlsx', getHeaders());
    } catch (e) { toast.error(e.message); }
  };

  const handleResync = async () => {
    setResyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/comp-off/resync`, {
        method: 'POST', headers: getHeaders(), credentials: 'include',
        body: JSON.stringify(selectedEntity ? { entity_id: selectedEntity.entity_id } : {}),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message);
      fetchRows();
    } catch (e) { toast.error(e.message); }
    finally { setResyncing(false); }
  };

  const handleRedate = async () => {
    if (!window.confirm(
      'Re-date comp-off validity?\n\nEvery credit will be valid from its worked date to the SAME DATE NEXT MONTH ' +
      '(e.g. worked 15 Jan → valid till 15 Feb). Credits that lapsed only because of the older 30-day rule become live again.'
    )) return;
    setRedating(true);
    try {
      const res = await fetch(`${API_URL}/api/comp-off/redate-expiries`, {
        method: 'POST', headers: getHeaders(), credentials: 'include', body: JSON.stringify({}),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message);
      fetchRows();
    } catch (e) { toast.error(e.message); }
    finally { setRedating(false); }
  };

  const openAdd = async () => {
    setAddForm({ employee_id: '', employee_name: '', add_days: '1', comp_off_date: today, reason: '' });
    setEmpSearch('');
    setAddOpen(true);
    try {
      const qs = selectedEntity
        ? `?entity_id=${selectedEntity.entity_id}&include_descendants=true`
        : '?is_active=true';
      const res = await fetch(`${API_URL}/api/employees${qs}`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setEmpList(Array.isArray(d) ? d : (d.employees || []));
      }
    } catch { /* keep dialog usable even if list fails */ }
  };

  const empName = (e) => [e.first_name, e.middle_name, e.last_name].filter(Boolean).join(' ') || e.employee_id;
  const empOptions = empList
    .filter(e => {
      const s = empSearch.toLowerCase();
      return !s || empName(e).toLowerCase().includes(s) || (e.employee_id || '').toLowerCase().includes(s);
    })
    .slice(0, 50);

  const submitAdd = async () => {
    if (!addForm.employee_id) { toast.error('Select a staff member'); return; }
    const days = parseFloat(addForm.add_days) || 0;
    if (days <= 0) { toast.error('Enter number of comp-off days (greater than 0)'); return; }
    setSaving(true);
    try {
      const params = new URLSearchParams({ leave_type: 'Compensatory Off', reason: addForm.reason || '' });
      params.append('add_days', days.toString());
      if (addForm.comp_off_date) params.append('comp_off_date', addForm.comp_off_date);
      const res = await fetch(`${API_URL}/api/employees/${addForm.employee_id}/leave-balance?${params}`, {
        method: 'PUT', headers: getHeaders(), credentials: 'include',
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed to add comp-off');
      toast.success(`Added ${days} comp-off day(s) for ${addForm.employee_name}`);
      setAddOpen(false);
      fetchRows();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const outOfSync = rows.filter(r => r.in_sync === false).length;

  return (
    <div className="space-y-4" data-testid="comp-off-register">
      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search staff..." className="pl-10" value={search}
            onChange={e => setSearch(e.target.value)} data-testid="co-register-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full md:w-48" data-testid="co-register-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All staff</SelectItem>
            <SelectItem value="with_balance">With balance</SelectItem>
            <SelectItem value="expiring_soon">Has expiring soon</SelectItem>
            <SelectItem value="expired">Has expired</SelectItem>
          </SelectContent>
        </Select>
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger className="w-full md:w-48" data-testid="co-register-dept-filter"><SelectValue placeholder="All departments" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-full md:w-48" data-testid="co-register-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="employee_list">Employee list order</SelectItem>
            <SelectItem value="name_asc">Name (A–Z)</SelectItem>
            <SelectItem value="name_desc">Name (Z–A)</SelectItem>
            <SelectItem value="department">Department</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="outline" className="border-slate-300">{filtered.length} staff</Badge>
        {canAdd && (
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={openAdd} data-testid="co-register-add-btn">
            <Plus className="w-4 h-4 mr-1.5" />Add Comp-Off
          </Button>
        )}
        <Button variant="outline" onClick={handleExcel} disabled={filtered.length === 0} data-testid="co-register-excel-btn">
          <Download className="w-4 h-4 mr-1.5" />Excel
        </Button>
        <Button variant="outline" onClick={handleResync} disabled={resyncing} data-testid="co-register-resync-btn">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${resyncing ? 'animate-spin' : ''}`} />Recalculate
        </Button>
        <Button variant="outline" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
          onClick={handleRedate} disabled={redating} data-testid="co-register-redate-btn">
          <CalendarClock className={`w-4 h-4 mr-1.5 ${redating ? 'animate-spin' : ''}`} />Fix Validity Dates
        </Button>
      </div>

      {outOfSync > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm"
          data-testid="co-register-drift-warning">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <span className="text-amber-800 dark:text-amber-200">
            {outOfSync} staff member{outOfSync > 1 ? 's have' : ' has'} comp-off credits that do not match their taken
            leaves. Tap <strong>Recalculate</strong> to rebuild the balances from the credit and leave records.
          </span>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />)}</div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Staff Name</TableHead>
                    <TableHead className="text-center">Comp-Offs Available<p className="text-[9px] font-normal text-slate-400">balance</p></TableHead>
                    <TableHead>Worked Date → Expiry <p className="text-[9px] font-normal text-slate-400">each credit</p></TableHead>
                    <TableHead className="text-center">Total Credited</TableHead>
                    <TableHead className="text-center">Taken</TableHead>
                    <TableHead className="text-center">Lapsed</TableHead>
                    <TableHead className="text-center">Remaining Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(r => (
                    <TableRow key={r.employee_id} data-testid={`co-register-row-${r.employee_id}`}>
                      <TableCell className="align-top">
                        <p className="text-sm font-medium">{r.employee_name}</p>
                        <code className="text-[10px] text-slate-400">{r.employee_id}</code>
                        <p className="text-[10px] text-slate-400">{r.department || ''}</p>
                      </TableCell>
                      <TableCell className="text-center align-top">
                        <span className={`text-lg font-bold ${r.balance > 0 ? 'text-emerald-600' : 'text-slate-300'}`}>{r.balance}</span>
                      </TableCell>
                      <TableCell className="align-top">
                        {(r.credits || []).length === 0 ? (
                          <span className="text-[11px] text-amber-600">No worked-date on record</span>
                        ) : (
                          <div className="space-y-1">
                            {r.credits.map(c => {
                              const noExpiry = (c.expires_at || '').startsWith('9999');
                              return (
                              <div key={c.credit_id}
                                className={`flex flex-wrap items-center gap-x-2 text-[11px] px-2 py-1 rounded ${
                                  c.status === 'expired' ? 'bg-slate-100 text-slate-400'
                                    : c.status === 'used' ? 'bg-blue-50 text-blue-700'
                                    : c.status === 'expiring_soon' ? 'bg-red-50' : 'bg-amber-50'}`}
                                data-testid={`co-credit-${c.credit_id}`}>
                                <span className={c.status === 'expired' ? 'line-through' : ''}>
                                  {c.worked_on
                                    ? <>Worked <strong>{c.worked_on}</strong> ({c.days}d)</>
                                    : <>Carried balance ({c.days}d) — no worked date on record</>}
                                </span>
                                {c.used_days > 0 && (
                                  <span className="text-blue-700">· taken {c.used_days}d</span>
                                )}
                                {c.remaining_days > 0 && c.status !== 'expired' && (
                                  <span className="text-emerald-700">· {c.remaining_days}d left</span>
                                )}
                                <span className={`font-semibold ml-auto ${
                                  c.status === 'expired' ? '' : c.status === 'used' ? 'text-blue-700'
                                    : c.status === 'expiring_soon' ? 'text-red-600' : 'text-amber-700'}`}>
                                  {c.status === 'used' ? 'Fully used'
                                    : c.status === 'expired' ? `Lapsed ${c.expires_at}`
                                    : noExpiry ? 'No expiry set'
                                    : `→ expires ${c.expires_at}${c.days_left != null && c.days_left <= 5 ? ` (${c.days_left}d left)` : ''}`}
                                </span>
                              </div>
                            );})}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center align-top text-sm">{r.total_credited}</TableCell>
                      <TableCell className="text-center align-top text-sm text-blue-700 font-medium">
                        {r.taken_days}
                        {r.in_sync === false && (
                          <p className="text-[9px] text-amber-600 font-normal">ledger {r.ledger_taken}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-center align-top text-sm text-slate-400">{r.lapsed_days ?? 0}</TableCell>
                      <TableCell className="text-center align-top">
                        <span className="text-sm font-semibold text-emerald-700">{r.balance}</span>
                        <span className="text-[10px] text-slate-400"> / {r.total}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-10 text-slate-500 text-sm">
                        <Clock3 className="w-6 h-6 mx-auto mb-2 text-slate-300" />
                        No comp-off records found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Comp-Off — adds a NEW credit without touching any existing comp-off */}
      <Dialog open={addOpen} onOpenChange={(v) => { if (!saving) setAddOpen(v); }}>
        <DialogContent className="max-w-lg" data-testid="co-register-add-dialog">
          <DialogHeader>
            <DialogTitle>Add Compensatory Off</DialogTitle>
            <DialogDescription>
              Credits a new comp-off for the selected staff member. Existing comp-off credits are kept exactly as they are —
              this only adds to the balance.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Staff member *</Label>
              {addForm.employee_id ? (
                <div className="flex items-center justify-between rounded-md border px-3 py-2 mt-1" data-testid="co-add-selected-staff">
                  <span className="text-sm font-medium">{addForm.employee_name} <code className="text-[10px] text-slate-400 ml-1">{addForm.employee_id}</code></span>
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setAddForm(f => ({ ...f, employee_id: '', employee_name: '' }))} data-testid="co-add-change-staff">Change</Button>
                </div>
              ) : (
                <>
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input placeholder="Search staff by name or ID..." className="pl-10" value={empSearch}
                      onChange={e => setEmpSearch(e.target.value)} data-testid="co-add-staff-search" />
                  </div>
                  <div className="mt-2 max-h-48 overflow-y-auto border rounded-md divide-y" data-testid="co-add-staff-list">
                    {empOptions.length === 0 ? (
                      <p className="text-xs text-slate-400 px-3 py-3 text-center">No staff found</p>
                    ) : empOptions.map(e => (
                      <button type="button" key={e.employee_id}
                        className="w-full text-left px-3 py-2 hover:bg-emerald-50 transition-colors"
                        onClick={() => setAddForm(f => ({ ...f, employee_id: e.employee_id, employee_name: empName(e) }))}
                        data-testid={`co-add-staff-${e.employee_id}`}>
                        <span className="text-sm">{empName(e)}</span>
                        <span className="text-[10px] text-slate-400 ml-2">{e.employee_id}{e.department ? ` · ${e.department}` : ''}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Comp-off days *</Label>
                <Input type="number" min="0.5" step="0.5" value={addForm.add_days}
                  onChange={e => setAddForm(f => ({ ...f, add_days: e.target.value }))}
                  placeholder="e.g. 1 or 0.5" data-testid="co-add-days" />
              </div>
              <div>
                <Label>Worked-on date</Label>
                <Input type="date" value={addForm.comp_off_date}
                  onChange={e => setAddForm(f => ({ ...f, comp_off_date: e.target.value }))}
                  data-testid="co-add-worked-date" />
              </div>
            </div>
            <p className="text-[11px] text-slate-400 -mt-2">Comp-off is valid for one month from the worked-on date.</p>
            <div>
              <Label>Reason / note</Label>
              <Input value={addForm.reason} onChange={e => setAddForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. Worked on Republic Day event" data-testid="co-add-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)} disabled={saving}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={submitAdd} disabled={saving} data-testid="co-add-submit">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
              Add Comp-Off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
